import React, { useState, useEffect, useMemo } from 'react';
import './HydrationTracker.css';

const DEFAULT_GOAL = 2000;
const MIN_GOAL = 500;
const MAX_GOAL = 6000;
const GLASS_AMOUNTS = [150, 250, 500];
const PRESET_GOALS = [1500, 2000, 2500, 3000];
const GOAL_STORAGE_KEY = 'hydrationGoal';

// ─── ICONS ───

function WaterDropIcon({ size = 20, color = '#60B5F0' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <path d="M12 2c-4 5.5-6 9-6 12a6 6 0 0 0 12 0c0-3-2-6.5-6-12z" />
    </svg>
  );
}

function PlusIcon({ size = 12, color = '#60B5F0' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function RefreshIcon({ size = 18, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

function EditIcon({ size = 18, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    </svg>
  );
}

function CloseIcon({ size = 18, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ─── WATER GLASS SVG ───

function WaterGlass({ percentage, width = 90, height = 120 }) {
  const glassMargin = 8;
  const glassWidth = width - glassMargin * 2;
  const glassHeight = height - 10;
  const cornerRadius = 14;
  const waterHeight = (percentage / 100) * (glassHeight - 20);
  const waterY = glassHeight - waterHeight - 4;

  // Unique id so multiple instances don't collide
  const clipId = useMemo(() => `glassClip-${Math.random().toString(36).slice(2, 9)}`, []);

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <defs>
        <clipPath id={clipId}>
          <rect x={glassMargin} y={6} width={glassWidth} height={glassHeight} rx={cornerRadius} />
        </clipPath>
      </defs>
      {/* Glass outline */}
      <rect
        x={glassMargin}
        y={6}
        width={glassWidth}
        height={glassHeight}
        rx={cornerRadius}
        className="hydration-glass-outline"
      />
      {/* Water fill */}
      <g clipPath={`url(#${clipId})`}>
        <rect
          x={glassMargin}
          y={waterY + 6}
          width={glassWidth}
          height={waterHeight + 10}
          fill="rgba(96, 181, 240, 0.35)"
        />
        <path
          d={`M${glassMargin} ${waterY + 6} Q${glassMargin + glassWidth * 0.25} ${waterY + 2}, ${glassMargin + glassWidth * 0.5} ${waterY + 6} T${glassMargin + glassWidth} ${waterY + 6} V${glassHeight + 10} H${glassMargin} Z`}
          fill="rgba(96, 181, 240, 0.25)"
        />
      </g>
      {/* Duck */}
      {percentage > 5 && <RubberDuck x={width / 2} y={waterY + 8} size={32} />}
      {/* Rim highlight TODO: DO WE NEED IT? */}
      <rect
        x={glassMargin + 1}
        y={10}
        width={0}
        height={glassHeight * 0.35}
        rx={2}
        fill="rgba(255,255,255,0.4)"
      />
    </svg>
  );
}

function RubberDuck({ x, y, size = 32 }) {
  const s = size / 36;
  return (
    <g transform={`translate(${x - size / 2}, ${y - size * 0.7})`}>
      <ellipse cx={18 * s} cy={24 * s} rx={14 * s} ry={10 * s} fill="#FFD54F" />
      <circle cx={18 * s} cy={12 * s} r={9 * s} fill="#FFD54F" />
      <ellipse cx={27 * s} cy={13 * s} rx={5 * s} ry={2.5 * s} fill="#FF9800" />
      <circle cx={21 * s} cy={10 * s} r={1.8 * s} fill="#3E2723" />
      <circle cx={21.8 * s} cy={9.2 * s} r={0.7 * s} fill="#FFF" />
      <ellipse
        cx={13 * s}
        cy={22 * s}
        rx={5 * s}
        ry={4 * s}
        fill="#FFC107"
        transform={`rotate(-15 ${13 * s} ${22 * s})`}
      />
      <ellipse cx={24 * s} cy={14 * s} rx={2 * s} ry={1.5 * s} fill="rgba(255,138,101,0.35)" />
    </g>
  );
}

// ─── GOAL EDIT MODAL ───

function GoalEditModal({ open, currentGoal, onClose, onSave }) {
  const [input, setInput] = useState(String(currentGoal));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setInput(String(currentGoal));
      setError(null);
    }
  }, [open, currentGoal]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const inputNum = parseInt(input, 10);

  const handleSave = () => {
    if (!Number.isFinite(inputNum)) {
      setError('Please enter a valid number.');
      return;
    }
    if (inputNum < MIN_GOAL || inputNum > MAX_GOAL) {
      setError(`Goal must be between ${MIN_GOAL} and ${MAX_GOAL} ml.`);
      return;
    }
    onSave(inputNum);
  };

  return (
    <div className="hydration-modal-overlay" onClick={onClose}>
      <div className="hydration-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="hydration-modal-header">
          <div className="hydration-modal-icon">
            <WaterDropIcon size={24} color="#60B5F0" />
          </div>
          <h3 className="hydration-modal-title">Set daily water goal</h3>
          <p className="hydration-modal-subtitle">
            Between {MIN_GOAL} ml and {MAX_GOAL} ml
          </p>
        </div>

        <div className="hydration-input-wrap">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            className="hydration-input"
            value={input}
            onChange={(e) => {
              setInput(e.target.value.replace(/[^0-9]/g, ''));
              setError(null);
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
            maxLength={5}
            autoFocus
            placeholder="2000"
          />
          <span className="hydration-input-unit">ml</span>
        </div>

        <div className="hydration-preset-row">
          {PRESET_GOALS.map((v) => (
            <button
              key={v}
              type="button"
              className={`hydration-preset ${inputNum === v ? 'is-active' : ''}`}
              onClick={() => { setInput(String(v)); setError(null); }}
            >
              {v} ml
            </button>
          ))}
        </div>

        {error && <div className="hydration-error">{error}</div>}

        <div className="hydration-modal-actions">
          <button type="button" className="hydration-btn hydration-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="hydration-btn hydration-btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── STORAGE ───
function todayIntakeKey() {
  return `hydration_${new Date().toISOString().split('T')[0]}`;
}

// ─── MAIN ───

function HydrationTracker() {
  const [intake, setIntake] = useState(0);
  const [goal, setGoal] = useState(DEFAULT_GOAL);
  const [goalModalOpen, setGoalModalOpen] = useState(false);

  // Load persisted state on mount
  useEffect(() => {
    try {
      const savedIntake = localStorage.getItem(todayIntakeKey());
      if (savedIntake) setIntake(parseInt(savedIntake, 10) || 0);

      const savedGoal = localStorage.getItem(GOAL_STORAGE_KEY);
      if (savedGoal) {
        const parsed = parseInt(savedGoal, 10);
        if (Number.isFinite(parsed) && parsed >= MIN_GOAL && parsed <= MAX_GOAL) {
          setGoal(parsed);
        }
      }
    } catch {}
  }, []);

  const addWater = (amount) => {
    const newIntake = intake + amount;
    setIntake(newIntake);
    try {
      localStorage.setItem(todayIntakeKey(), String(newIntake));
    } catch {}
  };

  const resetToday = () => {
    setIntake(0);
    try {
      localStorage.removeItem(todayIntakeKey());
    } catch {}
  };

  const saveGoal = (newGoal) => {
    setGoal(newGoal);
    try {
      localStorage.setItem(GOAL_STORAGE_KEY, String(newGoal));
    } catch {}
    setGoalModalOpen(false);
  };

  const percentage = goal > 0 ? Math.min((intake / goal) * 100, 100) : 0;
  const remaining = Math.max(goal - intake, 0);

  return (
    <div className="hydration-card">
      <div className="hydration-header">
        <div className="hydration-header-left">
          <WaterDropIcon size={20} color="#60B5F0" />
          <span className="hydration-title">Hydration</span>
        </div>
        <div className="hydration-header-right">
          <button
            type="button"
            className="hydration-icon-btn"
            onClick={() => setGoalModalOpen(true)}
            aria-label="Edit goal"
            title="Edit daily goal"
          >
            <EditIcon size={18} />
          </button>
          <button
            type="button"
            className="hydration-icon-btn"
            onClick={resetToday}
            aria-label="Reset today"
            title="Reset today"
          >
            <RefreshIcon size={18} />
          </button>
        </div>
      </div>

      <div className="hydration-body">
        <div className="hydration-glass-wrap">
          <WaterGlass percentage={percentage} width={90} height={120} />
        </div>

        <div className="hydration-right">
          <div>
            <div className="hydration-intake-row">
              <span className="hydration-intake-value">{intake}</span>
              <span className="hydration-intake-unit">ml</span>
            </div>
            <button
              type="button"
              className="hydration-goal-link"
              onClick={() => setGoalModalOpen(true)}
            >
              of {goal} ml goal
              <span className="hydration-goal-edit"> · edit</span>
            </button>
          </div>

          <span className="hydration-remaining">
            {remaining > 0 ? `${remaining} ml to go` : 'Goal reached! 🎉'}
          </span>

          <div className="hydration-buttons-row">
            {GLASS_AMOUNTS.map((amount) => (
              <button
                key={amount}
                type="button"
                className="hydration-add-btn"
                onClick={() => addWater(amount)}
              >
                <PlusIcon size={11} color="#60B5F0" />
                <span>{amount}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <GoalEditModal
        open={goalModalOpen}
        currentGoal={goal}
        onClose={() => setGoalModalOpen(false)}
        onSave={saveGoal}
      />
    </div>
  );
}

export default HydrationTracker;